import { Facebook, Mail, Phone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const TikTokIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M16.5 3c.3 2.1 1.5 3.6 3.6 3.9v2.5c-1.2.1-2.4-.2-3.6-.8v6.3c0 3.4-2.6 5.6-5.7 5.6-3 0-5.3-2.2-5.3-5.2 0-3.1 2.5-5.3 5.9-5v2.7c-.4-.1-.9-.2-1.3-.2-1.4 0-2.4 1-2.4 2.4 0 1.4 1 2.4 2.4 2.4 1.5 0 2.5-1 2.5-2.9V3h3.4z" />
  </svg>
);

const SOCIALS = [
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@nga_kakano_wahinenz",
    Icon: TikTokIcon,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61591576185549",
    Icon: Facebook,
  },
];

const NAV = [
  { label: "Why This Matters", href: "#why" },
  { label: "Our Vision", href: "#vision" },
  { label: "How It Works", href: "/how-it-works", route: true },
  { label: "Founder", href: "#founder" },
  { label: "Connect", href: "#connect" },
];

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (link) => {
    if (link.route) {
      navigate(link.href);
      window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      return;
    }
    document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="relative bg-forest-deep text-cream/80 border-t border-cream/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        {/* Whakataukī */}
        <div className="text-center max-w-3xl mx-auto pb-16 border-b border-cream/10">
          <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-light italic text-gold leading-snug">
            “Whāia te iti kahurangi ki te tūohu koe me he maunga teitei.”
          </p>
          <p className="mt-4 text-cream/50 font-light text-sm">
            Seek the treasure you value most dearly — if you bow your head, let
            it be to a lofty mountain.
          </p>
        </div>

        <div className="pt-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <img
              src="/nkw-logo-256.png"
              alt="Ngā Kākano Wāhine"
              className="h-24 w-24 rounded-full ring-1 ring-gold/30 mb-6"
            />
            <h3 className="font-serif-display text-2xl tracking-tight">
              Ngā Kākano Wāhine
            </h3>
            <p className="overline text-gold/70 text-[0.6rem] mt-1">
              Growing enterprise. Growing independence. Growing generations.
            </p>
            <p className="mt-6 text-cream/50 font-light text-sm max-w-xs leading-relaxed">
              An emerging Ōtautahi social enterprise creating pathways toward
              self-employment and business ownership for wāhine.
            </p>
          </div>

          <div>
            <p className="overline text-cream/40 mb-5">Tirohia — Explore</p>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <button
                    data-testid={`footer-link-${n.href.replace(/[#/]/g, "")}`}
                    onClick={() => go(n)}
                    className="text-cream/70 hover:text-gold font-light text-sm link-underline"
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="overline text-cream/40 mb-5">Whakapā mai — Connect</p>
            <a
              href="mailto:info@ngakakanowahine.co.nz"
              data-testid="footer-email"
              className="flex items-center gap-2 text-cream/70 hover:text-gold font-light text-sm mb-3 link-underline w-fit"
            >
              <Mail size={16} strokeWidth={1.5} /> info@ngakakanowahine.co.nz
            </a>
            <a
              href="tel:+6421083 55676"
              data-testid="footer-phone"
              className="flex items-center gap-2 text-cream/70 hover:text-gold font-light text-sm mb-6 link-underline w-fit"
            >
              <Phone size={16} strokeWidth={1.5} /> 021 0835 5676
            </a>
            <div className="flex gap-4">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-10 h-10 border border-cream/20 flex items-center justify-center text-cream/70 hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <Icon size={17} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-cream/10 flex flex-col sm:flex-row justify-between gap-4 text-cream/40 text-xs font-light">
          <p>© {new Date().getFullYear()} Ngā Kākano Wāhine. A movement for generations.</p>
          <p>Made with aroha in Aotearoa.</p>
        </div>
      </div>
    </footer>
  );
};
